'use client';

import { useEffect, useRef } from 'react';

import { LOGIN_TESTIMONIALS } from './login-testimonials';

type MaterializeCarouselInstance = {
  destroy: () => void;
};

type MaterializeCarouselApi = {
  init: (el: Element, options?: Record<string, unknown>) => MaterializeCarouselInstance;
  getInstance: (el: Element) => MaterializeCarouselInstance | null;
};

const CAROUSEL_OPTIONS = { padding: 200 };

export function LoginTestimonialCarousel() {
  const carouselRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const carouselEl = carouselRef.current;
    if (!carouselEl) {
      return undefined;
    }

    let cancelled = false;
    let retryTimer = 0;

    const destroyCarousel = () => {
      const materialize = (window as Window & { M?: { Carousel: MaterializeCarouselApi } }).M;
      const existing = materialize?.Carousel?.getInstance(carouselEl);
      existing?.destroy();
      carouselEl.classList.remove('carousel-ready');
    };

    const initCarousel = () => {
      const materialize = (window as Window & { M?: { Carousel: MaterializeCarouselApi } }).M;
      if (!materialize?.Carousel || cancelled) {
        return false;
      }

      destroyCarousel();
      carouselEl.style.visibility = 'visible';
      materialize.Carousel.init(carouselEl, CAROUSEL_OPTIONS);
      carouselEl.classList.add('carousel-ready');
      return true;
    };

    const handleResize = () => {
      if (cancelled) {
        return;
      }
      initCarousel();
    };

    const tryInitCarousel = () => {
      if (cancelled) {
        return;
      }
      if (initCarousel()) {
        return;
      }
      retryTimer = window.setTimeout(tryInitCarousel, 50);
    };

    tryInitCarousel();
    window.addEventListener('resize', handleResize);

    return () => {
      cancelled = true;
      window.clearTimeout(retryTimer);
      window.removeEventListener('resize', handleResize);
      destroyCarousel();
    };
  }, []);

  return (
    <div className="testimonial">
      <div className="carousel-container">
        <h5>What our clients say about us.</h5>
        <div className="carousel kw-login-carousel" ref={carouselRef}>
          {LOGIN_TESTIMONIALS.map((item) => (
            <div key={item.name} className="carousel-item">
              <div className="card">
                {/* eslint-disable-next-line @next/next/no-img-element */}
                <img src="/login-assets/images/Vector.svg" alt="" className="teampng" />
                <div className="feedback-content">
                  <div className="stars">
                    <span>&#9733;</span>
                    <span>&#9733;</span>
                    <span>&#9733;</span>
                    <span>&#9733;</span>
                    <span>&#9733;</span>
                  </div>
                  <p className={item.textClass}>{item.text}</p>
                </div>
                <div className="client-profile">
                  <h4>{item.name}</h4>
                  {/* eslint-disable-next-line @next/next/no-img-element */}
                  <img src={item.image} alt={item.name} className="testimonial-img" />
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
